import React, { useMemo } from "react";
import { Activity, Zap, Clock3, AlertTriangle, TrendingUp } from "lucide-react";

const _ms = (v) => (typeof v === "number" ? v : Date.parse(v || ""));

/**
 * Derive momentum from real activity signals:
 * - Strong Progress: ≥3 distinct activity entries in last 24h
 * - Active Today    : ≥1 activity in last 24h
 * - Slowing         : last activity 2–4 days ago
 * - No movement     : last activity > 4 days ago
 * - Multiple blockers detected: ≥2 overdue tasks
 */
const deriveMomentum = (activity = [], openTasks = []) => {
  const now = Date.now();
  const overdue = openTasks.filter((t) => t.due_at && _ms(t.due_at) < now).length;
  const last24h = activity.filter((a) => now - _ms(a.at) < 24 * 60 * 60 * 1000).length;
  const lastAt = _ms(activity[0]?.at);
  const daysSince = Number.isFinite(lastAt) ? Math.floor((now - lastAt) / 86400000) : 999;

  if (overdue >= 2) {
    return { id: "blockers", label: "Multiple blockers detected", tone: "red", Icon: AlertTriangle, sub: `${overdue} blocking actions outstanding` };
  }
  if (last24h >= 3) {
    return { id: "strong", label: "Strong progress", tone: "emerald", Icon: TrendingUp, sub: `${last24h} actions in the last 24 hours` };
  }
  if (last24h >= 1) {
    return { id: "active", label: "Active today", tone: "violet", Icon: Zap, sub: "Recent movement on this transaction" };
  }
  if (daysSince <= 4) {
    return { id: "slowing", label: `No movement for ${daysSince}d`, tone: "amber", Icon: Clock3, sub: "Push the next critical action to regain momentum" };
  }
  return { id: "stalled", label: `Stalled · ${daysSince}d quiet`, tone: "rose", Icon: AlertTriangle, sub: "Re-engage participants — chain health at risk" };
};

const TONE = {
  emerald: { card: "bg-emerald-50/70 border-emerald-200", icon: "bg-emerald-500/10 text-emerald-700", title: "text-emerald-900", sub: "text-emerald-800" },
  violet:  { card: "bg-indigo-50/70 border-indigo-200",   icon: "bg-indigo-500/10 text-indigo-700",   title: "text-indigo-900",  sub: "text-indigo-800" },
  amber:   { card: "bg-amber-50/70 border-amber-200",     icon: "bg-amber-500/10 text-amber-700",     title: "text-amber-900",   sub: "text-amber-800" },
  red:     { card: "bg-rose-50/70 border-rose-200",       icon: "bg-rose-500/10 text-rose-700",       title: "text-rose-900",    sub: "text-rose-800" },
  rose:    { card: "bg-rose-50/70 border-rose-200",       icon: "bg-rose-500/10 text-rose-700",       title: "text-rose-900",    sub: "text-rose-800" },
};

export const TransactionMomentum = ({ deal, openTasks = [] }) => {
  const m = useMemo(() => deriveMomentum(deal.activity || [], openTasks), [deal.activity, openTasks]);
  const tone = TONE[m.tone] || TONE.violet;
  const { Icon } = m;

  return (
    <section
      className={`rounded-xl border ${tone.card} px-4 py-3 flex items-center gap-3.5`}
      data-testid={`transaction-momentum-${m.id}`}
    >
      <div className={`w-9 h-9 rounded-lg ${tone.icon} flex items-center justify-center shrink-0`}>
        <Icon size={15} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Transaction momentum</p>
        <p className={`font-display text-[16px] font-bold tracking-tight ${tone.title} mt-0.5 leading-tight`} data-testid="transaction-momentum-label">
          {m.label}
        </p>
      </div>
      <p className={`hidden md:block text-[12px] ${tone.sub} max-w-[260px] text-right truncate`}>{m.sub}</p>
    </section>
  );
};

export default TransactionMomentum;
